import { useState, useCallback, useRef, useEffect } from 'react';
import {
  GripVertical,
  Circle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Trash2,
  Clock
} from 'lucide-react';
import RichTextEditor from '../common/RichTextEditor';
import LinkedItemsBadges from './LinkedItemsBadges';

const DURATION_OPTIONS = [5, 10, 15, 20, 30, 45, 60, 90, 120];

export default function AgendaItem({
  item,
  index,
  onUpdate,
  onDelete,
  onDragStart,
  onDragEnd,
  onDragOver,
  isDragging,
  isDragTarget,
  documents = [],
  policies = [],
  isExpanded,
  onToggleExpand,
  readOnly = false
}) {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [localTitle, setLocalTitle] = useState(item.title);
  const titleInputRef = useRef(null);
  const notesDebounceRef = useRef(null);

  // Sync local title when item changes
  useEffect(() => {
    setLocalTitle(item.title);
  }, [item.title]);

  // Focus title input when editing starts
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  const handleTitleBlur = useCallback(() => {
    setIsEditingTitle(false);
    if (localTitle.trim() && localTitle !== item.title) {
      onUpdate({ ...item, title: localTitle.trim() });
    } else {
      setLocalTitle(item.title);
    }
  }, [localTitle, item, onUpdate]);

  const handleTitleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleTitleBlur();
    } else if (e.key === 'Escape') {
      setLocalTitle(item.title);
      setIsEditingTitle(false);
    }
  }, [handleTitleBlur, item.title]);

  const handleToggleComplete = useCallback(() => {
    onUpdate({ ...item, isCompleted: !item.isCompleted });
  }, [item, onUpdate]);

  const handleDurationChange = useCallback((duration) => {
    onUpdate({ ...item, durationMinutes: duration });
  }, [item, onUpdate]);

  const handleNotesChange = useCallback((notes) => {
    // Clear existing debounce
    if (notesDebounceRef.current) {
      clearTimeout(notesDebounceRef.current);
    }

    // Debounce notes update
    notesDebounceRef.current = setTimeout(() => {
      onUpdate({ ...item, notes });
    }, 500);
  }, [item, onUpdate]);

  const handleLinkDocument = useCallback((docId) => {
    const linkedDocumentIds = item.linkedDocumentIds || [];
    if (!linkedDocumentIds.includes(docId)) {
      onUpdate({
        ...item,
        linkedDocumentIds: [...linkedDocumentIds, docId]
      });
    }
  }, [item, onUpdate]);

  const handleLinkPolicy = useCallback((policyId) => {
    const linkedPolicyIds = item.linkedPolicyIds || [];
    if (!linkedPolicyIds.includes(policyId)) {
      onUpdate({
        ...item,
        linkedPolicyIds: [...linkedPolicyIds, policyId]
      });
    }
  }, [item, onUpdate]);

  const handleRemoveDocument = useCallback((docId) => {
    const linkedDocumentIds = (item.linkedDocumentIds || []).filter(id => id !== docId);
    onUpdate({ ...item, linkedDocumentIds });
  }, [item, onUpdate]);

  const handleRemovePolicy = useCallback((policyId) => {
    const linkedPolicyIds = (item.linkedPolicyIds || []).filter(id => id !== policyId);
    onUpdate({ ...item, linkedPolicyIds });
  }, [item, onUpdate]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (notesDebounceRef.current) {
        clearTimeout(notesDebounceRef.current);
      }
    };
  }, []);

  const hasLinkedItems = (item.linkedDocumentIds?.length > 0) || (item.linkedPolicyIds?.length > 0);

  return (
    <div
      draggable={!readOnly && !isEditingTitle}
      onDragStart={(e) => {
        if (!readOnly) {
          e.dataTransfer.effectAllowed = 'move';
          onDragStart?.(index);
        }
      }}
      onDragEnd={() => onDragEnd?.()}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver?.(index);
      }}
      className={`rounded-lg border transition-all ${
        isDragging
          ? 'opacity-50 scale-95 border-accent shadow-lg'
          : isDragTarget
          ? 'border-accent border-2 border-dashed'
          : item.isCompleted
          ? 'bg-surface-hover border-border opacity-60'
          : 'bg-surface-card border-border'
      }`}
    >
      {/* Header Row */}
      <div className="flex items-center gap-2 p-3">
        {/* Drag Handle */}
        {!readOnly && (
          <div className="cursor-grab active:cursor-grabbing text-text-muted hover:text-text-tertiary">
            <GripVertical size={16} />
          </div>
        )}

        {/* Completion Toggle */}
        <button
          onClick={handleToggleComplete}
          disabled={readOnly}
          className="flex-shrink-0"
        >
          {item.isCompleted ? (
            <CheckCircle size={18} className="text-success" />
          ) : (
            <Circle size={18} className="text-text-tertiary hover:text-accent" />
          )}
        </button>

        {/* Title */}
        <div className="flex-1 min-w-0">
          {isEditingTitle ? (
            <input
              ref={titleInputRef}
              type="text"
              value={localTitle}
              onChange={(e) => setLocalTitle(e.target.value)}
              onBlur={handleTitleBlur}
              onKeyDown={handleTitleKeyDown}
              className="w-full text-sm bg-transparent border-b border-accent focus:outline-none text-text-primary"
            />
          ) : (
            <span
              onClick={() => !readOnly && setIsEditingTitle(true)}
              className={`text-sm cursor-text ${
                item.isCompleted
                  ? 'line-through text-text-tertiary'
                  : 'text-text-primary'
              }`}
            >
              {item.title || 'Untitled Item'}
            </span>
          )}
          {item.description && !isExpanded && (
            <p className="text-xs text-text-tertiary truncate mt-0.5">
              {item.description}
            </p>
          )}
        </div>

        {/* Duration */}
        {!readOnly ? (
          <select
            value={item.durationMinutes || 10}
            onChange={(e) => handleDurationChange(parseInt(e.target.value))}
            className="text-[10px] text-text-muted bg-surface-hover border-none rounded px-1.5 py-0.5 cursor-pointer focus:outline-none focus:ring-1 focus:ring-accent"
          >
            {DURATION_OPTIONS.map(dur => (
              <option key={dur} value={dur}>{dur} min</option>
            ))}
          </select>
        ) : (
          <span className="text-[10px] text-text-muted flex items-center gap-1">
            <Clock size={10} />
            {item.durationMinutes || 10} min
          </span>
        )}

        {/* Expand/Collapse Toggle */}
        <button
          onClick={onToggleExpand}
          className="p-1 text-text-tertiary hover:text-text-primary transition-colors"
        >
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>

        {/* Delete Button */}
        {!readOnly && (
          <button
            onClick={() => onDelete(item.id)}
            className="p-1 text-text-muted hover:text-red-500 transition-colors"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-3 pb-3 pt-0">
          {/* Description (if any) */}
          {item.description && (
            <p className="text-xs text-text-secondary mb-2 pl-8">
              {item.description}
            </p>
          )}

          {/* Rich Text Notes Editor */}
          <div className="pl-8">
            {readOnly ? (
              item.notes ? (
                <div className="text-sm text-text-secondary whitespace-pre-wrap p-2 bg-surface-elevated rounded-lg border border-border">
                  {item.notes}
                </div>
              ) : (
                <p className="text-xs text-text-muted italic">No notes</p>
              )
            ) : (
              <RichTextEditor
                content={item.notes || ''}
                onChange={handleNotesChange}
                onLinkDocument={handleLinkDocument}
                onLinkPolicy={handleLinkPolicy}
                documents={documents}
                policies={policies}
                placeholder="Add notes..."
              />
            )}

            {/* Linked Items Badges */}
            <LinkedItemsBadges
              linkedDocumentIds={item.linkedDocumentIds || []}
              linkedPolicyIds={item.linkedPolicyIds || []}
              documents={documents}
              policies={policies}
              onRemoveDocument={readOnly ? undefined : handleRemoveDocument}
              onRemovePolicy={readOnly ? undefined : handleRemovePolicy}
              readOnly={readOnly}
            />
          </div>
        </div>
      )}

      {/* Collapsed linked items indicator */}
      {!isExpanded && hasLinkedItems && (
        <div className="px-3 pb-2 pl-11">
          <LinkedItemsBadges
            linkedDocumentIds={item.linkedDocumentIds || []}
            linkedPolicyIds={item.linkedPolicyIds || []}
            documents={documents}
            policies={policies}
            readOnly={true}
          />
        </div>
      )}
    </div>
  );
}
